import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useId } from 'react';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { LoadingButton } from '@mui/lab';
import DockerAppTypes from 'constants/DockerAppTypes';
import { grey } from '@mui/material/colors';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import checkIsRequireTargetPort from 'redux/slices/dockerImage/helpers/checkIsRequireTargetPort';
import useGetAvailableDockerImage from 'redux/slices/dockerImage/hooks/useGetAvailableDockerImage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import PropTypes from 'prop-types';

const AddDockerImageForm = ({
  onCancel = () => {},
  submitForm = () => {},
  setForm = () => {},
  validateField = () => {},
  values = {},
  errors = {},
  loading = false,
  isUpgrade = false,
}) => {
  const nextTick = useNextTick();
  const {
    refetch,
    item: exists,
    loading: loadingAvailable,
    isLoaded,
  } = useGetAvailableDockerImage();
  const uniqueId = useId();

  const setFieldValue = async (field, value, isTrigger = true) => {
    setForm(field, value, false);
    if (isTrigger) {
      await nextTick();
      validateField(field);
    }
  };

  const handleBlurImageName = (e) => {
    e.target.value && refetch(e.target.value);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          mb: 4,
          height: 'calc(100% - 60px)',
          p: 4,
          ml: -4,
          mr: -4,
        }}
      >
        <Grid
          container
          spacing={5}
          sx={{ '& .Mui-disabled': { background: grey[50] } }}
        >
          <Grid item xs={12} sm={12}>
            <Typography variant='h3' mb={4}>
              Image Info
            </Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Image Name
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              value={values.ImageName ?? ''}
              error={(isLoaded && exists) || !!errors.ImageName}
              helperText={
                isLoaded && exists
                  ? 'Image Name is exists, try to change the other Image Name.'
                  : errors.ImageName
              }
              disabled={isUpgrade || loadingAvailable}
              placeholder='E.g. joblogicimage'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    {loadingAvailable ? (
                      <CircularProgress size={16} />
                    ) : (
                      isLoaded &&
                      !exists &&
                      !errors.ImageName && <CheckCircleIcon color='success' />
                    )}
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                !isUpgrade && setFieldValue('ImageName', e.target.value)
              }
              onBlur={(e) => !errors.ImageName && handleBlurImageName(e)}
            />
            <Box
              sx={{
                ml: 3,
                mt: 2,
                '& .MuiFormHelperText-root': { fontSize: 10 },
              }}
            >
              <FormHelperText>
                An initial alphanumeric part that may include separators like
                dot (.), underscore (_), or hyphen (-).
              </FormHelperText>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Deployment Type
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
            </Box>
            <Autocomplete
              disablePortal
              disabled={isUpgrade}
              value={values.DeploymentType ?? null}
              options={DockerAppTypes}
              getOptionLabel={(row) => row.Description}
              isOptionEqualToValue={(option, value) => {
                return option?.Value === value?.Value;
              }}
              renderOption={(props, option) => (
                <Box
                  component='li'
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  {...props}
                  key={option.Value}
                >
                  {option.Description}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.DeploymentType}
                  helperText={errors.DeploymentType}
                />
              )}
              onChange={(e, value) => {
                if (isUpgrade) {
                  return;
                }
                setFieldValue('DeploymentType', value);
                if (value?.IsCron) {
                  setFieldValue('TargetPort', '');
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Target Port
              {checkIsRequireTargetPort(values.DeploymentType) && (
                <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                  *
                </Box>
              )}
              <AppGlossary title='This is the port your container is listening on that will receive traffic. Set this value to the port number that your container uses. For HTTP ingress, your application ingress endpoint will always be exposed on port 443. For TCP ingress, configure the exposed port to customize your application ingress endpoint.' />
            </Box>
            <TextField
              type='number'
              disabled={!checkIsRequireTargetPort(values.DeploymentType)}
              fullWidth
              value={values.TargetPort ?? ''}
              placeholder='E.g. 80'
              error={
                checkIsRequireTargetPort(values.DeploymentType) &&
                !!errors.TargetPort
              }
              helperText={
                checkIsRequireTargetPort(values.DeploymentType) &&
                errors.TargetPort
              }
              onChange={(e) => setFieldValue('TargetPort', e.target.value)}
              sx={{ '& .Mui-disabled': { background: grey[50] } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Marketplace App
              {!isUpgrade && (
                <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                  *
                </Box>
              )}
              <AppGlossary title='Ensure all hosting resources are linked to a corresponding Shell App in the Marketplace to maintain centralized management and visibility' />
            </Box>
            <MarketplaceAppSelect
              value={values.AppModel ?? null}
              queryKey={uniqueId}
              disabled={isUpgrade}
              error={!!errors.AppModel}
              helperText={errors.AppModel}
              sx={{ '& .Mui-disabled': { background: grey[50] } }}
              onChange={(e, value) => setFieldValue('AppModel', value)}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Typography variant='h3' mb={4} mt={4}>
              Image Source
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Repository Url
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              value={values.RepositoryUrl ?? ''}
              error={!!errors.RepositoryUrl}
              helperText={errors.RepositoryUrl}
              placeholder='E.g. https://joblogic.com/marketplace/_git/demo-app'
              onChange={(e) => setFieldValue('RepositoryUrl', e.target.value)}
            />
            <Box
              sx={{
                ml: 3,
                mt: 2,
                '& .MuiFormHelperText-root': { fontSize: 10 },
              }}
            >
              <FormHelperText>
                Your URL may optionally start with http:// or https://.
              </FormHelperText>
              <FormHelperText>
                Your URL must include a valid domain name.
              </FormHelperText>
              <FormHelperText>
                Path (optional) - You can include an optional path after the
                domain.
              </FormHelperText>
              <FormHelperText>
                The URL cannot contain any spaces.
              </FormHelperText>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Branch Name
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              value={values.BranchName ?? ''}
              error={!!errors.BranchName}
              helperText={errors.BranchName}
              placeholder='E.g. joblogicbranchname'
              onChange={(e) => setFieldValue('BranchName', e.target.value)}
            />
            <Box
              sx={{
                ml: 3,
                mt: 2,
                '& .MuiFormHelperText-root': { fontSize: 10 },
              }}
            >
              <FormHelperText>No spaces allowed.</FormHelperText>
              <FormHelperText>
                Must contain at least one character.
              </FormHelperText>
              <FormHelperText>
                Cannot end with a period or a forward slash.
              </FormHelperText>
              <FormHelperText>
                Cannot contain special characters ( ~, ^, :, ?, *, [, or ]).
              </FormHelperText>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Dockerfile Path
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              value={values.DockerfilePath ?? ''}
              error={!!errors.DockerfilePath}
              helperText={errors.DockerfilePath}
              placeholder='E.g. path/to/Dockerfile'
              onChange={(e) => setFieldValue('DockerfilePath', e.target.value)}
            />
            <Box
              sx={{
                ml: 3,
                mt: 2,
                '& .MuiFormHelperText-root': { fontSize: 10 },
              }}
            >
              <FormHelperText>
                The regex ensures paths don&apos;t start with a slash (/).
              </FormHelperText>
              <FormHelperText>
                It supports paths with multiple directories, as long as the path
                doesn&apos;t start with a slash.
              </FormHelperText>
              <FormHelperText>
                The path should always end with Dockerfile
              </FormHelperText>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Access token
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
              <AppGlossary title='This is a Personal Access Token. These can be used instead of a password for applications like Git or can be passed in the authorization header to access REST APIs.' />
            </Box>
            <TextField
              fullWidth
              type={values.ShowPassword ? 'text' : 'password'}
              value={values.AccessToken ?? ''}
              error={!!errors.AccessToken}
              helperText={errors.AccessToken}
              placeholder='Enter your access token here'
              onChange={(e) => setFieldValue('AccessToken', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() =>
                        setFieldValue('ShowPassword', !values.ShowPassword)
                      }
                    >
                      {values.ShowPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Root Folder Path
            </Box>
            <TextField
              fullWidth
              value={values?.RootFolderPath ?? ''}
              placeholder='E.g. path/to/Folder'
              onChange={(e) => setFieldValue('RootFolderPath', e.target.value)}
            />
            <Box
              sx={{
                ml: 3,
                mt: 2,
                '& .MuiFormHelperText-root': { fontSize: 10 },
              }}
            >
            </Box>
          </Grid>
        </Grid>
      </Box>

      <FooterWrapper
        className='action-footer'
        sx={{
          backgroundColor: '#fff',
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: 0,
          p: 2,
          borderTop: `1px solid ${grey[200]}`,
          '& .MuiButtonBase-root': {
            minWidth: '80px',
          },
        }}
      >
        <Stack direction='row' justifyContent='end'>
          <Button
            variant='contained'
            color='secondary'
            disabled={loading}
            onClick={onCancel}
            sx={{ mr: 3 }}
          >
            Cancel
          </Button>

          <LoadingButton
            variant='contained'
            loading={loading}
            sx={{ mr: 3 }}
            onClick={() => {
              !isUpgrade
                ? !exists && !loadingAvailable && submitForm()
                : submitForm();
            }}
          >
            Save
          </LoadingButton>
        </Stack>
      </FooterWrapper>
    </Box>
  );
};

AddDockerImageForm.propTypes = {
  onCancel: PropTypes.func,
  submitForm: PropTypes.func,
  setForm: PropTypes.func,
  validateField: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  loading: PropTypes.bool,
  isUpgrade: PropTypes.bool,
};

export default React.memo(AddDockerImageForm);
